import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { DeleteDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { ModifyDashboardView } from '@futrli/views/views/reports/ModifyDashboardView/ModifyDashboardView';
import { useApiView } from '@futrli/views/hooks/useApiView';

interface DeleteDashboardDialogProps {
  dashboardId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteDashboardDialog = ({ dashboardId, isOpen, onClose }: DeleteDashboardDialogProps) => {
  const intl = useIntl();
  const { workspaceId = '', organisationId = '' } = useParams();

  const view = useApiView(
    useMemo(() => new ModifyDashboardView(workspaceId, organisationId, dashboardId), [
      workspaceId,
      organisationId,
      dashboardId
    ])
  );

  const { screen, handleAction, handleClose } = useDialogWizardState({
    isLoading: view.state.isLoading || view.state.isDeleting,
    error: view.state.errorDeleting,
    onAction: () => view.deleteDashboard(),
    onClose
  });

  return (
    <DeleteDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onDelete={handleAction}
      screen={screen}
      title={intl.formatMessage({ id: 'common.delete', defaultMessage: 'Delete' })}
      subtitle={view.state.dashboard?.name}
      thingsToKnow={[
        intl.formatMessage({
          id: 'predict.dashboards.delete.things_to_know.irreversible',
          defaultMessage: 'Deleting a dashboard is not recoverable'
        }),
        intl.formatMessage({
          id: 'predict.dashboards.delete.things_to_know.users_lose_access',
          defaultMessage: 'All users will immediately lose access to this dashboard'
        })
      ]}
      deleteDescription={intl.formatMessage({
        id: 'predict.dashboards.delete_dashboard_description',
        defaultMessage: 'Are you sure you want to delete this dashboard?'
      })}
      loadingMessage={
        view.state.isLoading
          ? intl.formatMessage({
              id: 'common.loading_please_wait',
              defaultMessage: 'Loading, please wait...'
            })
          : intl.formatMessage({
              id: 'predict.dashboards.deleting_dashboard',
              defaultMessage: 'Deleting dashboard...'
            })
      }
      successMessage={intl.formatMessage({
        id: 'predict.dashboards.dashboard_deleted',
        defaultMessage: 'Dashboard deleted'
      })}
      errorMessage={view.state.errorDeleting?.message}
      dataTestId="delete-dashboard-dialog"
    />
  );
};
